import React, {useEffect, useMemo, useState} from "react";
import {Table, Select, DatePicker, Button, Image, Tooltip, Row, Col, Empty} from "antd";
import SubCompanyInput from "../createDeliveryRequest/deliveryRequestForm/pickupInformation/subCompanyInput/SubCompanyInput";
import {useFetch} from "../../hooks/useFetch";
import {useStore} from "../../hooks/useStore";
import moment from "moment";
import {Link} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {FileSearchOutlined, DownloadOutlined, ExclamationCircleOutlined} from "@ant-design/icons";
import {DownloadButton} from "../_defaultComponents/DownloadButton/DownloadButton";
import {b64DecodeToUrl, truncateString} from "../../utils/helpers";
import {ConditionalTooltip} from "../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {ObjectionModal} from "./modals/ObjectionModal";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";

export const CreditLogTable = () => {
    const stores = useStore();
    const [selectedTransactionType, setSelectedTransactionType] = useState("all");
    const [selectedSubType, setSelectedSubType] = useState("all");
    const defaultDateRange = [moment().subtract(0, "days"), moment()];
    const [selectedDateRange, setSelectedDateRange] = useState(defaultDateRange);
    const [subCompanyId, setSelectedSubCompanyId] = useState(undefined);
    const [pageParam, setPageParam] = useState(1);
    const [excelData, setExcelData] = useState();
    const [loadingExcelData, setLoadingExcelData] = useState(false);
    const [updateFileUpdateVisibilty, setFileUpdateVisibility] = useState(false);
    const [orderId, setOrderId] = useState(null);
    const [deliveryId, setDeliveryId] = useState(null);
    const [downloadExcel, setDownloadExcel] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [subcompanyIdForObjection, setSubcompanyIdForObjection] = useState(null);
    const [paymentTransactionLogsLoading, paymentTransactionLogs, _, __, refetch] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyPaymentTransactionLogList.bind(null, {
            subCompanyId,
            skip: pageParam - 1,
            limit: 10,
            startDate:
                selectedDateRange?.length === 2 && selectedDateRange[0] && selectedDateRange[1]
                    ? moment(selectedDateRange[0]).format("YYYY-MM-DD")
                    : undefined,
            endDate:
                selectedDateRange?.length === 2 && selectedDateRange[1]
                    ? moment(selectedDateRange[1]).format("YYYY-MM-DD")
                    : undefined,
            transactionType:
                selectedTransactionType === "all" ? undefined : selectedTransactionType,
            processedSubType: selectedSubType === "all" ? undefined : selectedSubType
        }),
        initialCall: false,
        showLoading: true,
        dependencies: [
            subCompanyId,
            selectedTransactionType,
            selectedSubType,
            selectedDateRange,
            pageParam
        ]
    });

    const getExcelTemplateData = async () => {
        setLoadingExcelData(true);

        try {
            const response = await stores.companyStore.getSubCompanyPaymentTransactionLogList({
                subCompanyId,
                skip: 0,
                limit: -1,
                startDate:
                    selectedDateRange?.length === 2
                        ? moment(selectedDateRange[0]).format("YYYY-MM-DD")
                        : moment(Date.now())
                              .subtract(15, "days")
                              .format("YYYY-MM-DD"),
                endDate:
                    selectedDateRange?.length === 2
                        ? moment(selectedDateRange[1]).format("YYYY-MM-DD")
                        : moment(Date.now()).format("YYYY-MM-DD"),
                transactionType:
                    selectedTransactionType === "all" ? undefined : selectedTransactionType,
                processedSubType: selectedSubType === "all" ? undefined : selectedSubType,
                excel: true
            });
            const base64Data = response?.data?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);

            const href = blobUrl;
            const download = excelFileName;
            return {href, download};
        } catch (error) {
            console.log("error", error);
        } finally {
            setLoadingExcelData(false);
        }
    };

    const closeModals = () => {
        setFileUpdateVisibility(false);
    };

    const handleObjection = (orderId, deliveryId, subcompanyId) => {
        setOrderId(orderId);
        setDeliveryId(deliveryId);
        setFileUpdateVisibility(true);
        setSubcompanyIdForObjection(subcompanyId);
    };

    const handleObjectionModalClose = isSubmitted => {
        closeModals();
        if (isSubmitted) {
            setRefreshTrigger(prev => prev + 1);
        }
    };

    useEffect(() => {
        refetch();
    }, [refreshTrigger]);

    useEffect(() => {
        setExcelData();
    }, [subCompanyId, selectedTransactionType, selectedSubType, selectedDateRange, pageParam]);

    const excelFileName = useMemo(() => {
        let name = "";

        if (subCompanyId) {
            name +=
                stores.companyStore.subCompanyList.find(
                    subCompany => subCompany._id === subCompanyId
                )?.name + " ";
        }

        if (selectedDateRange?.length === 2) {
            name += ` ${moment(selectedDateRange[0]).format("DD.MM.YYYY")} - ${moment(
                selectedDateRange[1]
            ).format("DD.MM.YYYY")} `;
        }

        if (selectedTransactionType !== "all") {
            name += ` ${selectedTransactionType === "LOAD" ? "Yükleme" : "Harcama"} `;
        }

        if (selectedSubType !== "all") {
            name += ` ${(selectedSubType === "PAYMENT" && "Bakiye Yükleme") ||
                ("REFUND" && "İade") ||
                ("Teslimat" && "Sipariş")} `;
        }

        name = name + "Muhasebe Kredi Loglari.xlsx";

        return name;
    }, [subCompanyId, selectedTransactionType, selectedDateRange, selectedSubType]);

    //trigger when users all sub-companies are loaded
    useEffect(() => {
        if (stores.companyStore.subCompanyList?.length === 1) {
            setSelectedSubCompanyId(stores.companyStore.subCompanyList[0]._id);
        }
    }, [JSON.stringify(stores.companyStore.subCompanyList)]);

    const handleDownloadExcel = () => {
        getExcelTemplateData().then(({href, download}) => {
            if (href && download) {
                const link = document.createElement("a");
                link.href = href;
                link.download = download;
                link.click();
                setDownloadExcel(true);
            }
        });
    };

    return useObserver(() => (
        <div style={{width: "100%"}}>
            <div>
                <div style={{display: "none"}}>
                    {JSON.stringify(stores.companyStore.subCompanyList)}
                </div>
                <Row style={styles.headerContainer}>
                    <Col>
                        <div
                            style={{
                                flexDirection: "column",
                                display: "flex",
                                minWidth: 200,
                                margin: "10px 10px -10px 10px"
                            }}
                        >
                            <SubCompanyInput
                                onChange={value => setSelectedSubCompanyId(value)}
                                initialCall={true}
                                disabled={false}
                                value={subCompanyId}
                            />
                        </div>
                    </Col>
                    <Col>
                        <div style={styles.dateRangeContainer}>
                            <span>Başlangıç - Bitiş Tarihi</span>
                            <DatePicker.RangePicker
                                placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                                onChange={value => setSelectedDateRange(value)}
                                value={selectedDateRange}
                            />
                        </div>
                    </Col>
                    <Col>
                        <div style={styles.transactionTypeContainer}>
                            <span>İşlem Türü</span>
                            <Select
                                defaultValue="all"
                                options={[
                                    {
                                        value: "LOAD",
                                        label: "Yükleme"
                                    },
                                    {
                                        value: "USE",
                                        label: "Harcama"
                                    },
                                    {
                                        value: "OBJECTION",
                                        label: "İtiraz"
                                    },
                                    {
                                        value: "CASH",
                                        label: "Nakit Tahsilat"
                                    },
                                    {
                                        value: "CAMPAIGN",
                                        label: "Kampanya"
                                    },
                                    {
                                        value: "all",
                                        label: "Tümü"
                                    }
                                ]}
                                onChange={value => {
                                    setSelectedTransactionType(value);
                                    setSelectedSubType("all");
                                }}
                                value={selectedTransactionType}
                            />
                        </div>
                    </Col>
                    {stores.meStore.checkRole("isAdmin") ? (
                        <>
                            <Col>
                                <div style={styles.transactionTypeContainer}>
                                    <span>Ücret Tipi</span>
                                    <Select
                                        defaultValue="all"
                                        options={[
                                            {
                                                value: "all",
                                                label: "Tümü"
                                            },
                                            {
                                                value: "PAYMENT",
                                                label: "Bakiye Yükleme"
                                            },
                                            {
                                                value: "REFUND",
                                                label: "İade"
                                            },
                                            {
                                                value: "Teslimat",
                                                label: "Sipariş"
                                            },
                                            {
                                                value: "OBJECTION",
                                                label: "İtiraz"
                                            },
                                            {
                                                value: "CASH",
                                                label: "Nakit Tahsilat"
                                            }
                                        ]}
                                        onChange={value => setSelectedSubType(value)}
                                        value={selectedSubType}
                                    />
                                </div>
                            </Col>
                            <Col>
                                <div style={{margin: "10px"}}>
                                    <Tooltip title={"Excel Çıktısı İndir"}>
                                        <DownloadButton
                                            type="primary"
                                            icon={
                                                <DownloadOutlined spin={stores.appStore.loading} />
                                            }
                                            data-testid="downloadButton"
                                            loading={loadingExcelData}
                                            onClick={handleDownloadExcel}
                                        />
                                    </Tooltip>
                                </div>
                            </Col>
                        </>
                    ) : null}
                </Row>

                <Table
                    rowKey={(_, index) => index.toString()}
                    dataSource={paymentTransactionLogs?.data?.rows}
                    loading={paymentTransactionLogsLoading}
                    scroll={{x: "max-content"}}
                    pagination={{
                        total: paymentTransactionLogs?.data?.dataCount,
                        current: pageParam,
                        hideOnSinglePage: true,
                        showSizeChanger: false,
                        onChange: page => {
                            setPageParam(page);
                        }
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                    expandable={{
                        expandedRowRender: record => {
                            return (
                                <>
                                    {record.subTransactions &&
                                    record.subTransactions?.length > 0 ? (
                                        <Table
                                            dataSource={record.subTransactions}
                                            pagination={false}
                                            size="small"
                                            showHeader={false}
                                            rowClassName={(record, index) => {
                                                return "gray-background";
                                            }}
                                        >
                                            <Table.Column width={200} />
                                            {!subCompanyId && <Table.Column width={150} />}
                                            <Table.Column width={150} />
                                            <Table.Column width={150} />
                                            <Table.Column width={150} />
                                            <Table.Column
                                                key="subType"
                                                dataIndex="subType"
                                                width={150}
                                                maxWidth={150}
                                                title={"Ücret Tipi"}
                                                render={subType => (
                                                    <div
                                                        style={{
                                                            fontSize: "12px",
                                                            fontWeight: "bold",
                                                            marginLeft:
                                                                subType === "OBJECTION"
                                                                    ? "0px"
                                                                    : "20px"
                                                        }}
                                                    >
                                                        {(subType === "REFUND" && "İADE") ||
                                                            (subType === "OBJECTION" && "İTİRAZ") ||
                                                            (subType === "CASH" &&
                                                                "NAKİT TAHSİLAT") ||
                                                            subType}
                                                    </div>
                                                )}
                                            />
                                            <Table.Column width={200} />

                                            {stores.meStore.checkRole(
                                                "notVisibleForCompanyUser"
                                            ) && (
                                                <Table.Column
                                                    key="courierAmount"
                                                    width={150}
                                                    maxWidth={150}
                                                    dataIndex="courierAmount"
                                                    title={"Kurye Fiyatı"}
                                                    render={(courierAmount, record) => (
                                                        <div
                                                            style={{
                                                                fontSize: "12px",
                                                                fontWeight: "bold",
                                                                paddingLeft:
                                                                    record?.subType === "OBJECTION"
                                                                        ? "0px"
                                                                        : "16px"
                                                            }}
                                                        >
                                                            {courierAmount
                                                                ? `${courierAmount} TL`
                                                                : "-"}
                                                        </div>
                                                    )}
                                                />
                                            )}
                                            <Table.Column
                                                key="clientAmount"
                                                dataIndex="clientAmount"
                                                style={{paddingLeft: "20px !important"}}
                                                width={150}
                                                maxWidth={150}
                                                title={
                                                    !stores.meStore.checkRole(
                                                        "notVisibleForCompanyUser"
                                                    )
                                                        ? "Fiyat"
                                                        : "Müşteri Fiyatı"
                                                }
                                                render={(clientAmount, record) => (
                                                    <div
                                                        style={{
                                                            fontSize: "12px",
                                                            fontWeight: "bold",
                                                            paddingLeft:
                                                                record?.subType === "OBJECTION"
                                                                    ? "0px"
                                                                    : "16px"
                                                        }}
                                                    >
                                                        {clientAmount ? ` ${clientAmount} TL` : "-"}
                                                    </div>
                                                )}
                                            />
                                            <Table.Column width={200} />
                                            <Table.Column width={200} />
                                            <Table.Column width={200} />
                                            {!stores.meStore.checkRole("isCompanyUser") && (
                                                <Table.Column width={200} />
                                            )}
                                            <Table.Column width={60} />
                                        </Table>
                                    ) : record.transactionPhoto ? (
                                        <div>
                                            <h2>Fatura Görüntüsü </h2>
                                            {record.transactionPhoto.includes(".pdf") ||
                                            record.transactionPhoto.includes("buluttahsilat") ? (
                                                <a
                                                    href={record.transactionPhoto}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <Button type="primary">Dekont Görüntüle</Button>
                                                </a>
                                            ) : (
                                                <Image
                                                    src={record.transactionPhoto}
                                                    width={200}
                                                    height={200}
                                                    style={{
                                                        objectFit: "contain"
                                                    }}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        "Fatura Görüntüsü Bulunamadı"
                                    )}
                                </>
                            );
                        },
                        rowExpandable: record =>
                            (record.subTransactions && record.subTransactions?.length > 0) ||
                            (record?.processedSubType === "PAYMENT" &&
                                record?.transactionType === "LOAD")
                    }}
                >
                    <Table.Column
                        dataIndex="vigoOrderNumber"
                        width={200}
                        maxWidth={200}
                        title={"Sipariş Numarası"}
                        render={(_, record) => (
                            <span>
                                {record?.order !== null ? (
                                    record?.order?.vigoOrderNumber ? (
                                        <Link
                                            target="_blank"
                                            data-testid="orderNumber"
                                            style={{maxWidth: "470px"}}
                                            to={{
                                                pathname:
                                                    (stores.meStore.checkRole("isUatPoolCustomer")
                                                        ? "/musteri/siparislerim/"
                                                        : "/orders/") + record?.deliveryId
                                            }}
                                        >
                                            {record?.order?.vigoOrderNumber}
                                        </Link>
                                    ) : (
                                        <ConditionalTooltip
                                            condition={
                                                truncateString(
                                                    record?.order?.orderNumber,
                                                    16
                                                ).indexOf("...") > 0
                                            }
                                            text={record?.order?.orderNumber}
                                            child={
                                                <Link
                                                    target="_blank"
                                                    data-testid="orderNumber"
                                                    style={{maxWidth: "470px"}}
                                                    to={{
                                                        pathname:
                                                            (stores.meStore.checkRole(
                                                                "isUatPoolCustomer"
                                                            )
                                                                ? "/musteri/siparislerim/"
                                                                : "/orders/") + record?.deliveryId
                                                    }}
                                                >
                                                    {truncateString(record?.order?.orderNumber, 16)}
                                                </Link>
                                            }
                                        />
                                    )
                                ) : (
                                    "-"
                                )}
                            </span>
                        )}
                    />
                    {!subCompanyId && (
                        <Table.Column
                            key="subcompanyId"
                            dataIndex="subcompanyId"
                            width={150}
                            maxWidth={150}
                            title={"Detay"}
                            render={subcompanyId => (
                                <CustomButton
                                    href={"/panel/companies/sub-company/" + subcompanyId}
                                    data-testid="subcompanyDetail"
                                    onClick={event => {
                                        event.preventDefault();
                                        window.location.href =
                                            "/panel/companies/sub-company/" + subcompanyId;
                                    }}
                                    icon={<FileSearchOutlined />}
                                />
                            )}
                        />
                    )}
                    <Table.Column
                        key="courier"
                        dataIndex="courier"
                        title={"Kurye"}
                        width={150}
                        maxWidth={150}
                        render={courier => (
                            <span>
                                {courier &&
                                    (stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                                        <Link
                                            target="_blank"
                                            to={{
                                                pathname: "/couriers/" + courier?.id
                                            }}
                                        >
                                            {courier?.fullname}
                                        </Link>
                                    ) : (
                                        courier?.fullname ?? "Kurye"
                                    ))}
                            </span>
                        )}
                    />
                    <Table.Column
                        dataIndex="receiverName"
                        title={"Alıcı Adı"}
                        width={150}
                        maxWidth={150}
                    />
                    <Table.Column
                        dataIndex="createdAt"
                        title={"Tarih"}
                        width={150}
                        maxWidth={150}
                        render={date => <span>{moment(date).format("DD.MM.YYYY HH:mm")}</span>}
                    />
                    <Table.Column
                        dataIndex="transactionType"
                        title={"İşlem Türü"}
                        width={150}
                        maxWidth={150}
                        render={(transactionType, row) => (
                            <div>
                                {transactionType === "LOAD" ? (
                                    <span style={styles.loadContainer}>Yükleme</span>
                                ) : transactionType === "OBJECTION" ? (
                                    <span style={styles.objectionContainer}>İtiraz</span>
                                ) : transactionType === "CASH" ? (
                                    <span style={styles.loadContainer}>Nakit Tahsilat</span>
                                ) : transactionType === "CAMPAIGN" ? (
                                    <span style={styles.objectionContainer}>Kampanya</span>
                                ) : (
                                    <span style={styles.useContainer}>Harcama</span>
                                )}
                            </div>
                        )}
                    />
                    <Table.Column
                        dataIndex="processedSubType"
                        title={"Ücret Tipi"}
                        width={200}
                        maxWidth={200}
                        render={processedSubType => (
                            <span>
                                {(processedSubType === "PAYMENT" && "Bakiye Yükleme") ||
                                    (processedSubType === "REFUND" && "İade") ||
                                    (processedSubType === "Teslimat" && "Teslimat") ||
                                    (processedSubType === "OBJECTION" && "İtiraz") ||
                                    (processedSubType === "CASH" && "Nakit Tahsilat")}
                            </span>
                        )}
                    />
                    {stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                        <Table.Column
                            dataIndex="courierAmount"
                            title={"Kurye Fiyatı"}
                            width={150}
                            maxWidth={150}
                            render={courierAmount => (
                                <span>
                                    {courierAmount &&
                                        `${courierAmount?.toLocaleString("tr-TR", {
                                            maximumFractionDigits: 1
                                        })} TL`}
                                </span>
                            )}
                        />
                    ) : (
                        <></>
                    )}
                    <Table.Column
                        dataIndex="clientAmount"
                        title={"Müşteri Fiyatı"}
                        width={150}
                        maxWidth={150}
                        render={clientAmount => (
                            <span>
                                {clientAmount &&
                                    `${clientAmount?.toLocaleString("tr-TR", {
                                        maximumFractionDigits: 1
                                    })} TL`}
                            </span>
                        )}
                    />
                    <Table.Column
                        dataIndex="oldClientAmount"
                        title={"Eski Bakiye"}
                        width={150}
                        maxWidth={150}
                        render={oldClientAmount => (
                            <span>
                                {oldClientAmount &&
                                    `${oldClientAmount?.toLocaleString("tr-TR", {
                                        maximumFractionDigits: 1
                                    })} TL`}
                            </span>
                        )}
                    />
                    <Table.Column
                        dataIndex="newClientAmount"
                        title={"Yeni Bakiye"}
                        width={150}
                        maxWidth={150}
                        render={newClientAmount => (
                            <span>
                                {newClientAmount &&
                                    `${newClientAmount?.toLocaleString("tr-TR", {
                                        maximumFractionDigits: 1
                                    })} TL`}
                            </span>
                        )}
                    />
                    <Table.Column
                        dataIndex="description"
                        title={"Açıklama"}
                        width={200}
                        maxWidth={200}
                        render={description => <span>{description && `${description}`}</span>}
                    />
                    {!stores.meStore.checkRole("isCompanyUser") && (
                        <Table.Column
                            dataIndex="poolName"
                            title={"Havuz bölgesi"}
                            width={200}
                            maxWidth={200}
                            render={(poolName, record) => (
                                <span>
                                    {record?.order?.poolName
                                        ? record?.order?.poolName
                                        : record?.metaData?.poolName
                                        ? record?.metaData?.poolName
                                        : "-"}
                                </span>
                            )}
                        />
                    )}
                    {selectedTransactionType === "LOAD" && selectedSubType === "PAYMENT" ? null : (
                        <Table.Column
                            dataIndex="transactionType"
                            title={"İtiraz Bildir"}
                            width={150}
                            render={(_, record) => {
                                if (record.transactionType !== "LOAD" && !record.paymentObjection) {
                                    return stores.meStore.checkRole("createCreditLogObjection") ? (
                                        <CustomButton
                                            onClick={() =>
                                                handleObjection(
                                                    record?.order?._id,
                                                    record?.deliveryId,
                                                    record?.subcompanyId
                                                )
                                            }
                                            style={{borderRadius: "10px"}}
                                        >
                                            İtiraz Bildir <ExclamationCircleOutlined />
                                        </CustomButton>
                                    ) : (
                                        <></>
                                    );
                                }
                                if (record.paymentObjection) {
                                    const {status} = record.paymentObjection;

                                    if (status === "pending") {
                                        return (
                                            <CustomButton
                                                style={{
                                                    backgroundColor: "#ff9f43",
                                                    color: "white",
                                                    borderRadius: "10px"
                                                }}
                                                disabled
                                            >
                                                İnceleniyor
                                            </CustomButton>
                                        );
                                    }

                                    if (status === "approved") {
                                        return (
                                            <CustomButton
                                                style={{
                                                    backgroundColor: "#00b894",
                                                    color: "white",
                                                    borderRadius: "10px"
                                                }}
                                                disabled
                                            >
                                                Onaylandı
                                            </CustomButton>
                                        );
                                    }

                                    if (status === "rejected") {
                                        return (
                                            <CustomButton
                                                style={{
                                                    backgroundColor: "#ff5354",
                                                    color: "white",
                                                    borderRadius: "10px"
                                                }}
                                                disabled
                                            >
                                                Reddedildi
                                            </CustomButton>
                                        );
                                    }
                                }
                                return null;
                            }}
                        />
                    )}
                </Table>
            </div>
            <ObjectionModal
                subCompanyId={subcompanyIdForObjection}
                deliveryId={deliveryId}
                orderId={orderId}
                visibility={updateFileUpdateVisibilty}
                onCancel={handleObjectionModalClose}
            />
        </div>
    ));
};

const styles = {
    headerContainer: {
        gap: "20px",
        paddingBottom: 20,
        alignItems: "flex-end"
    },
    subCompanyContainer: {
        flexDirection: "column",
        display: "flex",
        minWidth: 200
    },
    dateRangeContainer: {
        flexDirection: "column",
        display: "flex",
        minWidth: 350,
        margin: "10px"
    },
    transactionTypeContainer: {
        flexDirection: "column",
        display: "flex",
        margin: "10px",
        minWidth: 250
    },
    loadContainer: {
        backgroundColor: "#00b894",
        padding: 5,
        color: "white",
        borderRadius: 5
    },
    useContainer: {
        backgroundColor: "#d63031",
        padding: 5,
        color: "white",
        borderRadius: 5
    },
    objectionContainer: {
        backgroundColor: "#ed9252",
        padding: 5,
        color: "white",
        borderRadius: 5
    }
};
